import React, { useState, useEffect, useMemo, useRef } from 'react';
import { isMotionScene } from '../../../features/scenarios/productionMode';
import { getVideoCreditRatePerSecond, hasVideoV2vRate } from './VideoGenerationSettings';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import { Tooltip } from '../../common/Tooltip';
import { ClickDropdown, HoverDropdownItem } from '../../common/HoverDropdown';
import { DurationStepper } from './DurationSelect';
import styles from './ScenarioGenerator.module.css';
import type { Scenario } from './types';
import { useSceneHandlers } from './hooks/SceneHandlersContext';
import { useSceneStateContext } from './hooks/SceneStateContext';
import { useStoryboardCompact } from './storyboardCompactContext';
import {
  findPreviousVideoLastFrame,
  isFaceSafeSeedanceScene,
  resolveLinkPreviousMode,
  resolveLinkPreviousMinInputSeconds,
  resolveLinkPreviousInputSeconds,
  resolveLinkPreviousInputFallbackSeconds,
  isLinkPreviousV2vReady,
  effectiveFrameLinkPreviousVideo,
  deriveSceneInputFamily,
  resolveUsableVideoModel,
  isBrandCutFromMaterial,
} from './utils';
import { useGenerationState } from '../state/useGenerationState';
import { SceneVideoBox } from './sceneItem/SceneVideoBox';
import { SceneFrameCarousel } from './sceneItem/SceneFrameCarousel';
import { SceneFrameTabs } from './sceneItem/SceneFrameTabs';
import { BrandCutMaterialThumb } from './sceneItem/BrandCutMaterialThumb';
import { SceneDetailColumn } from './sceneItem/SceneDetailColumn';
import { SceneFlowActions } from './sceneItem/SceneFlowActions';
import { MotionBrandCutButton } from './MotionBrandCutButton';
import { isMotionEngineScenario } from './motionScenario';
import { useSceneTimeline } from './sceneItem/useSceneTimeline';
import { useStoryboardMotionMode } from './hooks/useStoryboardMotionMode';
import { showStoryboardMotionSwitch } from './sceneItem/storyboardMotionLogic';
import { ThreeDotsIcon } from '../../common/selectIcons';

/** ブランドカット(タイトルカード/エンドカード)の表示秒数の選択肢 (1〜10秒) */
const BRAND_CUT_DURATION_OPTIONS = Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: `${i + 1}秒` }));
/** モーションシーンの尺の選択肢 (1〜60秒)。動画モデルの対応尺ではなくレンダラーの尺の範囲
 *  (backend motion_scene.SCENE_MOTION_MIN/MAX_SEC・video-editor の MOTION_MIN_DURATION_SEC) に合わせる */
const MOTION_SCENE_DURATION_OPTIONS = Array.from({ length: 60 }, (_, i) => ({ value: i + 1, label: `${i + 1}秒` }));

export interface SceneItemProps {
  // Core (行固有データのみ。共通ハンドラ/参照データは SceneHandlersContext から取る)
  scenario: Scenario;
  scene: Scenario['scenes'][number];
  sceneIndex: number;
  sceneKey: string;
  isFirstScene: boolean;
  isLastScene: boolean;
  nextSceneContinuesPrevious: boolean;
  // ストーリーボードモード用: true のときシーンカードを描画せず、動画生成ボックス
  // (Step 2) だけを単独で描画する。StoryboardCandidates 内に置くために使う。
  // 「動画を追加」で本編動画を分割する場合、担当範囲の開始シーンをアンカーとして
  // カードごとに 1 インスタンス描画される (scene にはアンカーシーンが渡る)。
  videoBoxOnly?: boolean;
  // ストーリーボードモードで絵コンテ未選択のときなど、videoBoxOnly の動画生成ボタンを
  // 非活性化したい場合に理由文を渡す。文言はそのままカスタムツールチップに表示する。
  videoBoxDisabledReason?: string;
  // 動画カードの表示番号 (1始まり) と総数。複数カード時のみヘッダーに番号を出す。
  videoCardIndex?: number;
  videoCardCount?: number;
  // 全動画カードのアンカーシーン order (表示順)。未生成カードも含むため、
  // 「前の動画とつなげる」参照元セレクトの「動画N」ラベルの番号付けに使う。
  videoCardOrders?: number[];
  // 追加カードの「特定シーンだけ生成」の既定開始シーン (= アンカーシーンの order)。
  // 保存済み範囲 (generation_metadata.scene_range) が無いときの初期値に使う。
  videoCardDefaultRangeStart?: number;
  // 既定の終了シーン。自動カード分割 (useAutoVideoCardSegmentation) が担当範囲の
  // 末尾を渡す。未指定なら従来通り最終本編シーンまで。
  videoCardDefaultRangeEnd?: number;
  // ローカル追加された未生成カードを取り除くハンドラ (WorkPanel が管理)。
  onRemoveVideoCard?: () => void;
}

export const SceneItem: React.FC<SceneItemProps> = ({
  scenario,
  scene,
  sceneIndex,
  sceneKey: key,
  isFirstScene,
  isLastScene,
  nextSceneContinuesPrevious,
  videoBoxOnly = false,
  videoBoxDisabledReason,
  videoCardIndex,
  videoCardCount,
  videoCardOrders,
  videoCardDefaultRangeStart,
  videoCardDefaultRangeEnd,
  onRemoveVideoCard,
}) => {
  // ストーリーボードの左列が畳まれている (= メイン領域が狭い) 間は、
  // シーン見出しを「シーン1」→「1」に簡略化する。
  const isCompactHeader = useStoryboardCompact();
  const {
    videoGenerationMode,
    videoSettings,
    avoidFaces,
    creditRatesData,
    creditsData,
    deletingScenes,
    addingScenes,
    swappingScenes,
    editingField,
    editingValue,
    setEditingField,
    setEditingValue,
    getPreviewWidth,
    getSceneVideoSettings,
    updateSceneVideoSettings,
    updateSceneStoryboardDuration,
    updateSceneBrandCutDuration,
    handleSaveField,
    handleSwapSceneOrder,
    handleAddSceneBelow,
    handleDeleteScene,
    deleteSceneMutation,
    createSceneMutation,
    isCreditInsufficient,
    isStorageExceeded,
    openPurchaseModal,
  } = useSceneHandlers();

  // シーンカードのコラプス状態。シナリオ生成完了時 (カードのマウント時) はどのモードでも開いておく。
  const [isSceneCollapsed, setIsSceneCollapsed] = useState(false);
  // ヘッダーの「すべて開く / すべて閉じる」(WorkPanel) の合図に揃える。マウント後に届いた合図だけに従い、
  // 以前のシナリオで押した合図 (マウント時点で既に version > 0) で新しいカードが畳まれないようにする。
  const { sceneCollapseSignal } = useSceneStateContext();
  const mountedCollapseVersionRef = useRef(sceneCollapseSignal.version);
  useEffect(() => {
    if (sceneCollapseSignal.version > mountedCollapseVersionRef.current) setIsSceneCollapsed(sceneCollapseSignal.collapsed);
  }, [sceneCollapseSignal]);


  const sceneSettings = getSceneVideoSettings(key);

  // このシーンの入力系統 (P2 step 3。'frame' | 'reference' | 'storyboard')。シナリオ単位の
  // videoGenerationMode (context) の代わりにこれを使うことで、「シーンごとに制作」の3択 UI
  // (後続 step) が scenes.video_input_mode を書くだけで混在シナリオに正しく反映される
  // (docs/features/scene-location-and-production-mode-plan.md §3.1/§3.3)。'extend' (延長)
  // は家系を持たない別軸のため、この値自体には現れない (utils.ts の deriveSceneInputFamily
  // 参照)。video_input_mode が全 NULL の現在は常にシナリオ単位の videoGenerationMode と一致する。
  const sceneFamily = deriveSceneInputFamily(scenario, scene);

  // このシーンの実効動画モデル・解像度（シーン単位の設定 > シナリオのデフォルト）
  const effectiveVideoModel = resolveUsableVideoModel(sceneSettings.videoModel, (videoSettings as any).videoModel);
  const effectiveResolution = sceneSettings.resolution || videoSettings.resolution || '1080';

  // ストーリーボードモードの動画カードでは、尺の選択肢を
  // 「シナリオ想定尺 <= d <= モデル最大」で絞り込む（想定尺 > モデル最大 のときは
  // モデル最大のみを許容）。モデルの supported_durations は generation-models から取る。
  const { data: videoModels = [] } = useGenerationModels('video');
  const currentVideoModel = useMemo(
    () => videoModels.find((m) => m.model_code === effectiveVideoModel),
    [videoModels, effectiveVideoModel],
  );
  const availableDurations = useMemo<number[]>(() => {
    const list = (currentVideoModel as { supported_durations?: number[] } | undefined)?.supported_durations;
    return Array.isArray(list) && list.length > 0 ? list : [4, 5, 6, 7, 8, 9, 10];
  }, [currentVideoModel]);
  // frame/reference (非ストーリーボード) の尺セレクタの選択肢。ストーリーボードの
  // videoBoxOnly (storyboardDurationOptions、想定尺への総尺連動あり) と違い、単純に
  // モデルの対応尺 (「自動」= -1 を除く正の値) を昇順で並べるだけ。AI がシーンごとに
  // duration_seconds を設定するようになったため (2026-08-30)、ここも実モデルの尺
  // レンジをそのまま選べるようにする必要がある。
  // モーションシーンは動画モデルを使わないので、レンダラーの尺の範囲 (1〜60秒) を出す。
  const isMotionSceneRow = isMotionScene(scene);
  const nonStoryboardDurationOptions = useMemo(() => (
    isMotionSceneRow
      ? MOTION_SCENE_DURATION_OPTIONS
      : [...availableDurations]
        .filter((d) => d > 0)
        .sort((a, b) => a - b)
        .map((v) => ({ value: v, label: `${v}秒` }))
  ), [availableDurations, isMotionSceneRow]);
  // タイムライン系の派生値 (コマ尺 / 実効尺 / 開始・終了秒 / 尺オプション)
  const {
    storyboardCutDurationOptions,
    storyboardCutValue,
    getSceneEffectiveDuration,
    storyboardStartSeconds,
    storyboardEndSeconds,
    brandCutHasBoundVideo,
    formatTimecode,
    storyboardDurationOptions,
    storyboardDefaultDuration,
    getStoryboardRangeDuration,
  } = useSceneTimeline({ scenario, scene, sceneIndex, sceneKey: key, videoBoxOnly, availableDurations });
  // 絵コンテの「モーションで上書き」(3 点メニューの入口。戻すのはモーションカードの ×。§10.13 4.)
  const storyboardMotion = useStoryboardMotionMode(scene, key);
  // 「既存の素材を挿入」で追加したブランドカット (素材がそのままクリップになり、生成系 UI は不要)
  const brandCutFromMaterial = isBrandCutFromMaterial(scene);

  // 初期既定を 1 度だけ適用したシーンを記録 (以後のユーザー選択を上書きしないため)
  const storyboardDurationInitedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!storyboardDurationOptions || storyboardDefaultDuration == null) return;
    // モデルの supported_durations が未取得の間は availableDurations がフォールバック値
    // ([4,5,6,7,8,9,10]) になり、既定尺が誤った値で DB 永続化され二度と補正されない。
    // 実モデルが解決するまで初期化を遅延する (解決後に再 fire される)。
    if (!currentVideoModel) return;
    const valid = storyboardDurationOptions.map((o) => o.value);
    if (!valid.includes(sceneSettings.duration)) {
      // モデル変更等で現在値が範囲外になったら既定尺へ揃える
      updateSceneVideoSettings(key, { duration: storyboardDefaultDuration });
      storyboardDurationInitedRef.current.add(key);
    } else if (
      !storyboardDurationInitedRef.current.has(key) &&
      scene.video_duration_seconds == null &&
      sceneSettings.sceneRangeEnabled === undefined
    ) {
      // 未保存シーンの初回のみ: 既定をタイムライン総尺カバー値にする。
      // 「特定シーンだけ生成」範囲が既に設定済み (自動カード分割等) のカードは、
      // 範囲から丸めた尺が正なので総尺カバー値で上書きしない。
      storyboardDurationInitedRef.current.add(key);
      if (sceneSettings.duration !== storyboardDefaultDuration) {
        updateSceneVideoSettings(key, { duration: storyboardDefaultDuration });
      }
    }
  }, [currentVideoModel, storyboardDurationOptions, storyboardDefaultDuration, sceneSettings.duration, sceneSettings.sceneRangeEnabled, scene.video_duration_seconds, key, updateSceneVideoSettings]);

  // frame/reference (非ストーリーボード): 保存済みシーン尺やモデル切替で現在値が
  // 実効モデルの対応尺レンジ外になったら範囲内へクランプする (上のストーリーボード用
  // 範囲外リセット effect と同趣旨。ストーリーボードの挙動はそちらのまま変更しない)。
  // 「タイムライン総尺カバー値」のような概念は無いため、単純に最も近い対応尺へスナップする。
  useEffect(() => {
    if (videoGenerationMode === 'storyboard') return;
    // モデル未解決の間は待つ (availableDurations がフォールバック値のため)
    if (!currentVideoModel) return;
    // 既に動画が紐づいているシーンでは触らない。生成済みシーンの video_duration_seconds は
    // 「次に生成するときの尺(リクエスト値)」ではなく「生成された動画の実測尺の記録」であり、
    // useSceneTimeline / エクスポート (FCPXML 等) / BGM 尺計算がタイムラインの正として最優先で
    // 読む値。例えば Seedance 2.5 (4〜30秒) で15秒の動画を生成済みのシーンでモデルを
    // gemini-omni-flash (3〜10秒) に切り替えると、このガードが無いと実尺15秒のまま
    // duration だけ10秒へスナップされ DB に書き戻ってしまい (updateSceneVideoSettings が
    // video_duration_seconds を上書き)、タイムラインが実際の動画尺と乖離して壊れる。
    if (scene.video_material_id) return;
    const positives = nonStoryboardDurationOptions.map((o) => o.value);
    if (positives.length === 0 || positives.includes(sceneSettings.duration)) return;
    const clamped = Math.min(positives[positives.length - 1], Math.max(positives[0], sceneSettings.duration));
    const snapped = positives.includes(clamped)
      ? clamped
      : positives.reduce((best, d) => (Math.abs(d - clamped) < Math.abs(best - clamped) ? d : best), positives[0]);
    updateSceneVideoSettings(key, { duration: snapped });
  }, [videoGenerationMode, currentVideoModel, nonStoryboardDurationOptions, sceneSettings.duration, scene.video_material_id, key, updateSceneVideoSettings]);

  // ストーリーボード動画カードの「特定シーンだけ生成」範囲の復元。
  // 優先順: 保存済み範囲 (generation_metadata.scene_range) > カードの既定範囲
  // (自動分割の担当範囲、または追加カードのアンカーシーン〜最終シーン)。
  // どちらも無ければ従来通り範囲なし (本編全体)。
  // 上の duration 初期化 effect より後に宣言し、範囲から丸めた尺で上書きさせる。
  // skipPersist: 復元しただけで video_duration_seconds を DB へ書き戻さない。
  //
  // ⚠️ 「一度きり」の ref 制御は使わず、条件駆動 (範囲が未設定の間は毎レンダー再試行)
  // にしている。一度きり方式は、既定範囲が後から届く (自動カード分割) ケースや
  // effect の実行順・再マウントに対して脆く、初回の空振りで復元経路が死ぬ。
  // sceneRangeEnabled が true/false いずれかになった時点 (復元適用 or ユーザー操作)
  // で条件が外れて再試行は止まる。
  useEffect(() => {
    if (!videoBoxOnly || videoGenerationMode !== 'storyboard') return;
    // モデル未解決の間は尺の丸め先 (対応尺リスト) がフォールバック値になるため待つ
    if (!currentVideoModel) return;
    if (sceneSettings.sceneRangeEnabled !== undefined) return;
    const savedRange = (scene.generation_metadata as { scene_range?: { start?: number; end?: number } } | undefined)?.scene_range;
    const bodyOrders = (scenario.scenes || [])
      .filter((s) => !s.brand_cut_position)
      .map((s) => s.order)
      .sort((a, b) => a - b);
    const maxBodyOrder = bodyOrders.length > 0 ? bodyOrders[bodyOrders.length - 1] : undefined;
    const rawStart = Number(savedRange?.start ?? videoCardDefaultRangeStart ?? NaN);
    const rawEnd = Number(savedRange?.end ?? videoCardDefaultRangeEnd ?? maxBodyOrder ?? NaN);
    if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd) || bodyOrders.length === 0) return;
    // 保存済み範囲はシーン削除・挿入で order が変わっても追従しないため、実在する
    // 本編シーンの order へスナップする (存在しない番号のままだとセレクトに表示できない)。
    // 開始は「保存値以上で最小の order」、終了は「保存値以下で最大の order」に寄せる。
    const start = bodyOrders.find((o) => o >= rawStart) ?? bodyOrders[bodyOrders.length - 1];
    const end = [...bodyOrders].reverse().find((o) => o <= rawEnd) ?? bodyOrders[0];
    const rangeEnd = Math.max(start, end);
    const { duration } = getStoryboardRangeDuration(start, rangeEnd);
    updateSceneVideoSettings(
      key,
      { sceneRangeEnabled: true, sceneRangeStart: start, sceneRangeEnd: rangeEnd, duration },
      { skipPersist: true },
    );
  });

  // Seedance + フレームモード + 顔あり (has_visible_face・欠損は顔あり扱い) の
  // セクション開始シーンでは、フォトリアルな顔含み画像を first_frame として渡せない
  // (BytePlus face_upload_restricted)。フレームは絵コンテ用イラストとして生成し、
  // r2v の構図参照として渡す。動画の last_frame は以降の continues_previous_shot=true
  // シーンに連鎖させる。この条件下ではドラッグ&ドロップ/素材選択のみ非活性化する
  // (フレーム生成ボタンは有効 = イラスト生成)。
  const { visualStyleIsStylized } = useGenerationState();
  // 「前の動画とつなげる」の消費ポイント見積りで、生成直後 (シナリオ再取得前) の
  // ラストフレームをローカル state から拾うために使う (payload 構築と同じフォールバック)。
  const { sceneLastFrames } = useSceneStateContext();
  const blockFirstFrameInput = isFaceSafeSeedanceScene(
    effectiveVideoModel, sceneFamily, avoidFaces, isFirstScene,
    scene.continues_previous_shot ?? false,
    scene.has_visible_face,
    visualStyleIsStylized,
  );

  // フレームモードの「延長」実効判定 (docs/features/
  // link-previous-scene-continuity-default-plan.md §9 step-1 項目3)。表示
  // (SceneFrameCarousel のプレースホルダ切替)・課金見積り (sceneCredits) の両方で使う。
  const frameLinkActive = effectiveFrameLinkPreviousVideo({
    videoGenerationMode: sceneFamily,
    scenario,
    scene,
    linkPreviousVideoSetting: sceneSettings.linkPreviousVideo,
    videoModel: effectiveVideoModel,
    videoModels,
  });

  // このシーンの消費クレジット計算（モデル×解像度×音声で切り替え）
  const sceneCredits = useMemo(() => {
    // モーショングラフィックスのシーンは定額 (credit_rate_config の video_generation / motion-graphics-scene・
    // docs/features/brand-cut-motion-graphics-plan.md §10.4)。未設定なら 0 (SceneVideoBox が送らせない)
    if (isMotionScene(scene)) {
      const motionRate = creditRatesData?.rates?.find(
        (r: { service_type: string; resource_type: string; credits_per_unit: number }) => r.service_type === 'video_generation' && r.resource_type === 'motion-graphics-scene',
      );
      return motionRate ? Number(motionRate.credits_per_unit) : 0;
    }
    const duration = sceneSettings.duration;
    // 「前の動画とつなげる」ON の対応モデル (frame_anchor_v2v=Seedance 2.5系 /
    // v2v_only=MiniMax H3) は前の動画を参照動画 (v2v) として合算課金するため、
    // (出力秒数 + 入力秒数) × v2v レートで見積もる (BE resolve_input_video_seconds と
    // 同じ式。docs/features/link-previous-video-multi-model-plan.md §5.1・§5.3)。
    // 入力秒数の最低計上はモデル別 (frame_anchor_v2v のみ最低4秒。resolveLinkPrevious
    // MinInputSeconds 参照。ハードコードの max(4,…) は撤去)。
    // 発動判定は生成 payload 構築 (useVideoHandlers) と同じ条件に揃える: frame_anchor_v2v
    // はフレームが解決できれば前の動画の尺が未取得でも v2v を採用する (メタデータ欠落
    // 素材の自己修復含む) ため isLinkPreviousV2vReady で判定する。v2v レートが未登録の
    // モデル (extend 等) は getVideoCreditRatePerSecond が通常レートへ静かにフォール
    // バックするため、hasVideoV2vRate で明示的に検知して通常見積りへ落とす (発動しない)。
    // frame モードは frameLinkActive (実効判定) を使う (§9 step-1 項目6。見積りと
    // payload が同じ実効判定を使うことで金額が一致する)。
    if (
      ((sceneFamily === 'storyboard' || sceneFamily === 'reference')
        && sceneSettings.linkPreviousVideo)
      || frameLinkActive
    ) {
      const linkPrevMode = resolveLinkPreviousMode(effectiveVideoModel, videoModels);
      const prevInfo = linkPrevMode
        ? findPreviousVideoLastFrame(scenario.id, scenario.scenes || [], scene.order)
        : null;
      // 生成動画の終わり絵を優先し、ユーザーの「最後のフレーム」枠は無いときだけ (SceneVideoBox と同順)
      const prevFrameId = prevInfo?.lastFrameMaterialId || (prevInfo?.prevSceneKey
        ? sceneLastFrames[prevInfo.prevSceneKey]?.materialId
        : undefined);
      // 参照元シーンの明示指定 (§10 B案) があればその動画の尺で見積もる。
      // v2v_only は明示指定があれば直前の生成を待たずに繋がるため、発動判定にも渡す。
      const sourceScene = sceneSettings.linkPreviousSourceSceneId
        ? (scenario.scenes || []).find((s) => s.id === sceneSettings.linkPreviousSourceSceneId && !!s.video_material_id)
        : undefined;
      const v2vReady = !!prevInfo && isLinkPreviousV2vReady(linkPrevMode, prevInfo.videoGenerated, prevFrameId, !!sourceScene);
      if (
        v2vReady
        && hasVideoV2vRate(creditRatesData?.rates, effectiveVideoModel, effectiveResolution, sceneSettings.generateAudio)
      ) {
        const v2vRate = getVideoCreditRatePerSecond(
          creditRatesData?.rates,
          effectiveVideoModel,
          effectiveResolution,
          sceneSettings.generateAudio,
          true,
        );
        const inputDuration = sourceScene
          ? sourceScene.video_duration_seconds
          : prevInfo!.prevVideoDurationSeconds;
        // 実測尺 (inputDuration) が不明なら、参照元シーンの動画カード尺設定を近似値と
        // して使う (useVideoHandlers.ts の handleGenerateSceneVideo と同じ引数で呼び、
        // 両見積りを一致させる)。
        const fallbackInputSeconds = resolveLinkPreviousInputFallbackSeconds(
          scenario.id,
          sourceScene?.order,
          prevInfo!.prevSceneKey,
          getSceneVideoSettings,
        );
        const minBilledInputSeconds = resolveLinkPreviousMinInputSeconds(linkPrevMode, effectiveVideoModel, videoModels);
        const inputSeconds = resolveLinkPreviousInputSeconds(minBilledInputSeconds, inputDuration, fallbackInputSeconds);
        return (duration + inputSeconds) * v2vRate;
      }
    }
    const rate = getVideoCreditRatePerSecond(
      creditRatesData?.rates,
      effectiveVideoModel,
      effectiveResolution,
      sceneSettings.generateAudio,
    );
    return duration * rate;
  }, [
    creditRatesData, sceneSettings.duration, sceneSettings.generateAudio,
    sceneSettings.linkPreviousVideo, sceneSettings.linkPreviousSourceSceneId,
    effectiveVideoModel, effectiveResolution,
    sceneFamily, videoModels, scenario.id, scenario.scenes, scene.order, scene.video_input_mode,
    sceneLastFrames, getSceneVideoSettings, frameLinkActive,
  ]);
  // 絵コンテのモーション上書き (§10.13) の料金行 = モーションシーンと同じ定額 (motion-graphics-scene)。
  // 2 択が「絵コンテ動画のまま」の間も (切り替えた直後にカードが出る) 同じ値を渡せるよう sceneCredits とは別に引く
  const storyboardMotionCredits = useMemo(() => {
    if (videoGenerationMode !== 'storyboard') return 0;
    const motionRate = creditRatesData?.rates?.find(
      (r: { service_type: string; resource_type: string; credits_per_unit: number }) => r.service_type === 'video_generation' && r.resource_type === 'motion-graphics-scene',
    );
    return motionRate ? Number(motionRate.credits_per_unit) : 0;
  }, [creditRatesData, videoGenerationMode]);
  const sceneTotalAvailable = creditsData?.total_available ?? creditsData?.credits_balance ?? 0;
  const isSceneCreditInsufficient =
    !!creditsData && sceneCredits > 0 && sceneTotalAvailable < sceneCredits;
  const isDeleting = deletingScenes.has(scene.id);
  const isAdding = addingScenes.has(scene.id);

  // Step 2「動画」生成ボックス。通常はシーンカード内に描画するが、ストーリーボード
  // モードでは videoBoxOnly で StoryboardCandidates 内に描画する（動画カードごとに 1 つ）。
  const videoStepBox = (
    <SceneVideoBox
      scenario={scenario}
      scene={scene}
      sceneKey={key}
      videoBoxDisabledReason={videoBoxDisabledReason}
      blockFirstFrameInput={blockFirstFrameInput}
      frameLinkActive={frameLinkActive}
      effectiveVideoModel={effectiveVideoModel}
      effectiveResolution={effectiveResolution}
      sceneCredits={sceneCredits}
      isSceneCreditInsufficient={isSceneCreditInsufficient}
      sceneSettings={sceneSettings}
      storyboardDurationOptions={videoGenerationMode === 'storyboard' ? storyboardDurationOptions : nonStoryboardDurationOptions}
      getStoryboardRangeDuration={getStoryboardRangeDuration}
      videoCardIndex={videoCardIndex}
      videoCardCount={videoCardCount}
      videoCardOrders={videoCardOrders}
      onRemoveVideoCard={onRemoveVideoCard}
    />
  );

  // ストーリーボードモード: シーンカードを描かず動画生成ボックスのみを単独描画する
  if (videoBoxOnly) {
    return (
      <div className={styles.flowActionsContainer}>
        {videoStepBox}
      </div>
    );
  }

  return (
                        <li key={key} data-scene-id={scene.id} className={`${styles.sceneItem} ${isDeleting ? styles.sceneItemDeleting : ''} ${isAdding ? styles.sceneItemAdding : ''}`}>
                          <div className={styles.sceneHeaderRow}>
                            {editingField?.sceneId === scene.id && editingField?.field === 'setting' ? (
                              <>
                                <span className={`${styles.sceneOrderBadge} ${scene.brand_cut_position ? styles.sceneOrderBadgeBrandCut : ''}`}>
                                  {isCompactHeader ? scene.order : `シーン ${scene.order}`}
                                </span>
                                <div className={styles.inlineEditContainerHeader} data-scene-inline-edit>
                                  <textarea
                                    className={styles.inlineEditTextareaHeader}
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setEditingField(null);
                                        setEditingValue('');
                                      } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                        e.preventDefault();
                                        handleSaveField(scene.id, 'setting', editingValue);
                                      }
                                    }}
                                    autoFocus
                                    rows={1}
                                  />
                                  <div className={styles.inlineEditActionsHeader}>
                                    <button
                                      type="button"
                                      className={styles.inlineEditCancel}
                                      onClick={() => {
                                        setEditingField(null);
                                        setEditingValue('');
                                      }}
                                      title="キャンセル (Esc)"
                                    >
                                      キャンセル
                                    </button>
                                    <button
                                      type="button"
                                      className={styles.inlineEditSave}
                                      onClick={() => handleSaveField(scene.id, 'setting', editingValue)}
                                      title="保存"
                                    >
                                      保存
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div
                                className={styles.sceneToggleArea}
                                onClick={() => setIsSceneCollapsed((c) => !c)}
                                role="button"
                                aria-expanded={!isSceneCollapsed}
                              >
                                <span className={`${styles.sceneOrderBadge} ${scene.brand_cut_position ? styles.sceneOrderBadgeBrandCut : ''}`}>
                                  {isCompactHeader ? scene.order : `シーン ${scene.order}`}
                                </span>
                                <div className={styles.sceneTitleWrapper}>
                                  <span className={styles.sceneTitleText}>
                                    {scene.setting || '（未設定）'}
                                  </span>
                                  <button
                                    type="button"
                                    className={styles.fieldEditButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingField({ sceneId: scene.id, field: 'setting' });
                                      setEditingValue(scene.setting || '');
                                    }}
                                    title="場所・設定を編集"
                                  >
                                    <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
                                  </button>
                                </div>
                              </div>
                            )}
                            {/* 絵コンテのコマ尺セレクト + タイムライン上の開始〜終了秒。
                                ストーリーボードモードのみ。アコーディオンのタイトル行
                                (移動ボタンの左) に置く。シナリオ生成時に AI が提案した秒数が
                                初期値で、変更するとストーリーボード画像生成のプロンプトに反映。 */}
                            {videoGenerationMode === 'storyboard' && (
                              <div
                                className={`${styles.sceneHeaderDivider} ${styles.sceneHeaderDividerStatic}`}
                                aria-hidden="true"
                              />
                            )}
                            {/* ストーリーボードモードのコマ尺 + タイムコード。
                                ブランドカット (タイトルカード/エンドカード) は秒数の正本が
                                brand_cut_duration_seconds なので、コマ尺ではなくブランドカット
                                表示秒数のステッパーを 1 本だけ出す (タイムコードは共通)。
                                動画素材が紐付いている場合は実尺固定なので読み取り専用表示。 */}
                            {videoGenerationMode === 'storyboard' && (
                              <div
                                className={styles.sceneHeaderDuration}
                                onClick={(e) => e.stopPropagation()}
                                title={
                                  scene.brand_cut_position
                                    ? (brandCutHasBoundVideo ? '紐付け動画の実尺で表示されます' : 'ブランドカットの表示秒数')
                                    : undefined
                                }
                              >
                                {scene.brand_cut_position ? (
                                  brandCutHasBoundVideo ? (
                                    /* 紐付け動画の実尺固定で編集不可だが、一般シーンと
                                       秒数の縦位置・左の区切り線を揃えるため、同じ
                                       DurationStepper を非活性 (disabled) で出す。 */
                                    <DurationStepper
                                      value={Math.round(getSceneEffectiveDuration(scene))}
                                      onChange={() => {}}
                                      options={BRAND_CUT_DURATION_OPTIONS}
                                      disabled
                                    />
                                  ) : (
                                    <DurationStepper
                                      value={scene.brand_cut_duration_seconds ?? 3}
                                      onChange={(v) => updateSceneBrandCutDuration(scene.id, v)}
                                      options={BRAND_CUT_DURATION_OPTIONS}
                                    />
                                  )
                                ) : (
                                  <DurationStepper
                                    value={storyboardCutValue}
                                    onChange={(v) => updateSceneStoryboardDuration(key, v)}
                                    options={storyboardCutDurationOptions}
                                  />
                                )}
                                <span className={styles.sceneHeaderTimeRange}>
                                  <span>{formatTimecode(storyboardStartSeconds)}</span>
                                  <span>{formatTimecode(storyboardEndSeconds)}</span>
                                </span>
                              </div>
                            )}
                            {/* ストーリーボード以外のモードでは、ブランドカットの表示秒数だけ
                                編集できるようにステッパーを出す (タイムコードは無し)。動画素材が
                                紐付いている場合は実尺固定なので何も出さない。 */}
                            {videoGenerationMode !== 'storyboard' && scene.brand_cut_position && !brandCutHasBoundVideo && (
                              <div
                                className={styles.sceneHeaderDuration}
                                onClick={(e) => e.stopPropagation()}
                                title="ブランドカットの表示秒数"
                              >
                                <DurationStepper
                                  value={scene.brand_cut_duration_seconds ?? 3}
                                  onChange={(v) => updateSceneBrandCutDuration(scene.id, v)}
                                  options={BRAND_CUT_DURATION_OPTIONS}
                                />
                              </div>
                            )}
                            {/* 全編モーション (§10.5): ブランドカットもモーションで作る (計画の尺で固定) */}
                            {scene.brand_cut_position && isMotionEngineScenario(scenario) && (
                              <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 8 }}>
                                <MotionBrandCutButton scenario={scenario} scene={scene} />
                              </div>
                            )}
                            {videoGenerationMode === 'storyboard' ? (
                              /* ストーリーボードでは上下移動・削除を 3 点メニューに集約し、
                                 アイコンのみ・常時表示にする。 */
                              <ClickDropdown
                                position="bottom"
                                align="right"
                                minWidth="180px"
                                usePortal
                                trigger={
                                  <button
                                    type="button"
                                    className={styles.sceneMenuButton}
                                    title="シーンの操作"
                                    aria-label="シーンの操作"
                                  >
                                    <ThreeDotsIcon vertical />
                                  </button>
                                }
                              >
                                <HoverDropdownItem
                                  onClick={() => handleSwapSceneOrder(scenario.id, scene.id, scenario.scenes[sceneIndex - 1].id)}
                                  disabled={isFirstScene || swappingScenes || !!scene.brand_cut_position}
                                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6" /></svg>}
                                >
                                  シーンを上に移動
                                </HoverDropdownItem>
                                <HoverDropdownItem
                                  onClick={() => handleSwapSceneOrder(scenario.id, scene.id, scenario.scenes[sceneIndex + 1].id)}
                                  disabled={isLastScene || swappingScenes || !!scene.brand_cut_position}
                                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>}
                                >
                                  シーンを下に移動
                                </HoverDropdownItem>
                                {/* エンドカードの下に挿入すると末尾ブランドカットより後ろになるため不可 */}
                                <HoverDropdownItem
                                  onClick={() => handleAddSceneBelow(scenario.id, scene.id)}
                                  disabled={createSceneMutation.isPending || scene.brand_cut_position === 'end'}
                                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>}
                                >
                                  シーンを下に追加
                                </HoverDropdownItem>
                                {/* 絵コンテの本編シーン: モーションで上書き (選ぶとモーションカードが出る。戻すのはカードの ×) */}
                                {showStoryboardMotionSwitch(videoGenerationMode, scene) && !storyboardMotion.isMotion && (
                                  <HoverDropdownItem
                                    onClick={() => { void storyboardMotion.setMode('motion'); }}
                                    disabled={storyboardMotion.pending}
                                    icon={<img src="/scene_motion.svg" alt="" style={{ width: '14px', height: '14px' }} />}
                                  >
                                    モーションで上書き
                                  </HoverDropdownItem>
                                )}
                                <HoverDropdownItem
                                  onClick={() => handleDeleteScene(scenario.id, scene.id)}
                                  disabled={deleteSceneMutation.isPending}
                                  icon={<img src="/bin_1.svg" alt="" style={{ width: '14px', height: '14px' }} />}
                                >
                                  シーンを削除
                                </HoverDropdownItem>
                              </ClickDropdown>
                            ) : (
                              <>
                                <div className={styles.sceneHeaderDivider} aria-hidden="true" />
                                <div className={styles.sceneOrderButtons}>
                                  <Tooltip content="シーンを上に移動">
                                    <button
                                      type="button"
                                      className={styles.sceneOrderButton}
                                      onClick={() => handleSwapSceneOrder(scenario.id, scene.id, scenario.scenes[sceneIndex - 1].id)}
                                      disabled={isFirstScene || swappingScenes || !!scene.brand_cut_position}
                                    >
                                      ▲
                                    </button>
                                  </Tooltip>
                                  <Tooltip content="シーンを下に移動">
                                    <button
                                      type="button"
                                      className={styles.sceneOrderButton}
                                      onClick={() => handleSwapSceneOrder(scenario.id, scene.id, scenario.scenes[sceneIndex + 1].id)}
                                      disabled={isLastScene || swappingScenes || !!scene.brand_cut_position}
                                    >
                                      ▼
                                    </button>
                                  </Tooltip>
                                </div>
                                <button
                                  type="button"
                                  className={styles.deleteSceneButton}
                                  onClick={() => handleDeleteScene(scenario.id, scene.id)}
                                  disabled={deleteSceneMutation.isPending}
                                  title="シーンを削除"
                                >
                                  <img src="/bin_1.svg" alt="削除" style={{ width: '16px', height: '16px' }} />
                                </button>
                              </>
                            )}
                          </div>

                          <div className={styles.sceneContent} style={isSceneCollapsed ? { display: 'none' } : undefined}>
                            {/* Step 1: プレビュー画像とフレーム生成ボタン
                                ストーリーボードモードでは個別フレーム画像の概念が無くなるため、
                                フレームのプレースホルダ列ごと非表示にする。 */}
                            {videoGenerationMode !== 'storyboard' && (
                              <div
                                className={styles.scenePreviewColumn}
                                style={{
                                  minWidth: `${getPreviewWidth(videoSettings.aspectRatio)}px`,
                                  maxWidth: `${getPreviewWidth(videoSettings.aspectRatio)}px`,
                                }}
                              >
                                {/* 素材から追加したブランドカット (タイトル/エンドカード) は素材 (動画/静止画) が
                                    そのままクリップになるのでフレーム生成は意味を持たない。プレースホルダの
                                    代わりに素材のサムネイルを出す。 */}
                                {brandCutFromMaterial ? (
                                  <BrandCutMaterialThumb scene={scene} sceneKey={key} size="fill" />
                                ) : (
                                  <>
                                    <SceneFrameCarousel
                                      scenario={scenario}
                                      scene={scene}
                                      sceneKey={key}
                                      blockFirstFrameInput={blockFirstFrameInput}
                                      frameLinkActive={frameLinkActive}
                                    />

                                    <SceneFrameTabs
                                      scenario={scenario}
                                      scene={scene}
                                      sceneKey={key}
                                      nextSceneContinuesPrevious={nextSceneContinuesPrevious}
                                      blockFirstFrameInput={blockFirstFrameInput}
                                      frameLinkActive={frameLinkActive}
                                    />
                                  </>
                                )}
                                {(isCreditInsufficient || isStorageExceeded) && (
                                  <div className={styles.generateGuidance}>
                                    {isCreditInsufficient && (
                                      <div>ポイントが不足しています。<button type="button" onClick={openPurchaseModal} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>ポイントを購入</button>してください。</div>
                                    )}
                                    {isStorageExceeded && (
                                      <div>ストレージ容量の上限に達しました。<a href="/trash">ごみ箱</a>を空にすると空き容量を確保できます。プランのアップグレードで容量を引き上げることも可能です。<a href="/subscription">プランを確認</a></div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 右列: シーン詳細と商品トグル・最後のフレーム */}
                            <div className={styles.sceneDetailsColumn}>
                              <SceneDetailColumn
                                scene={scene}
                                sceneKey={key}
                                scenario={scenario}
                                brandCutHasBoundVideo={brandCutHasBoundVideo}
                                brandCutFromMaterial={brandCutFromMaterial}
                              />

                              <SceneFlowActions
                                scenario={scenario}
                                scene={scene}
                                sceneKey={key}
                                /* 素材から追加したブランドカットは動画生成の対象外 (生成ボタン・
                                   オプションの三点メニュー・尺ステッパーとも不要) なので Step 2 を出さない */
                                videoStepBox={brandCutFromMaterial ? null : videoStepBox}
                                /* 絵コンテのモーション上書き (§10.13) の定額。sceneCredits は motion シーンなら
                                   credit_rate_config の motion-graphics-scene をそのまま返す */
                                storyboardMotionCredits={storyboardMotionCredits}
                              />
                            </div>
                          </div>
                        </li>
  );
};
